/**
 * ExploreView — the v3 Explore homepage: project header + stats, a "Designs"
 * tree of brief REVISIONS (one collapsible folder each, with its own slot/Roll
 * pool), and the design inspector. Empty project shows a Create-brief CTA.
 */
import { observer } from "mobx-react-lite";
import { useState } from "react";
import type { GeneratedItem } from "src/types/GeneratedItem";
import { Icon, ACCENT } from "./Icon";
import type { BoardState } from "./BoardState";
import type { BriefSectionState } from "./briefModel";
import { DesignThumb } from "./DesignThumb";

const NAMES = [
  "Aspen",
  "Birch",
  "Cedar",
  "Dahlia",
  "Elder",
  "Fenn",
  "Garnet",
  "Hollow",
  "Iris",
  "Juniper",
  "Kestrel",
  "Larch",
  "Marsh",
  "Nimbus",
  "Onyx",
  "Poplar",
  "Quartz",
  "Rowan",
  "Sable",
  "Thorn",
];

export interface DesignRowData {
  item: GeneratedItem;
  key: string;
  name: string;
  units: number;
  gia: number;
  nia: number;
  cost: number;
  co2: number;
  buildings: number;
  maxFloors: number;
  leaders: string[];
}

const SORTS = [
  { key: "gia", label: "GIA (high → low)", get: (d: DesignRowData) => d.gia, dir: -1 },
  { key: "cost", label: "Cost (low → high)", get: (d: DesignRowData) => d.cost, dir: 1 },
  { key: "units", label: "Units (high → low)", get: (d: DesignRowData) => d.units, dir: -1 },
  { key: "co2", label: "CO₂ (low → high)", get: (d: DesignRowData) => d.co2, dir: 1 },
] as const;

type SortKey = (typeof SORTS)[number]["key"];

const int = (n: number) => Math.round(n).toLocaleString();
const eurM = (n: number) => "€" + (n / 1e6).toFixed(1) + "M";

const icons = {
  bookmark: <path d="M6 3h12v18l-6-4-6 4z" />,
  lock: (
    <g>
      <rect x="5" y="11" width="14" height="9" rx="1.5" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </g>
  ),
  lockOpen: (
    <g>
      <rect x="5" y="11" width="14" height="9" rx="1.5" />
      <path d="M8 11V8a4 4 0 0 1 7.5-2" />
    </g>
  ),
  x: <path d="M6 6l12 12M18 6L6 18" />,
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  edit: <path d="M4 20h4l10-10-4-4L4 16z" />,
  folder: <path d="M3 6a1 1 0 0 1 1-1h5l2 2h8a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />,
  chev: <path d="M6 9l6 6 6-6" />,
  check: <path d="M5 13l4 4 10-10" />,
  flag: <path d="M5 21V4M5 4h11l-2 4 2 4H5" />,
  arrowR: <path d="M5 12h14M13 6l6 6-6 6" />,
  star: <path d="M12 3l2.6 5.6 6.1.8-4.5 4.2 1.2 6.1L12 17l-5.4 2.9 1.2-6.1L3.3 9.4l6.1-.8z" />,
  generate: (
    <g>
      <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6z" />
      <path d="M18 14l.7 2.1L21 17l-2.3.9L18 20l-.7-2.1L15 17l2.3-.9z" />
    </g>
  ),
};

export function deriveRows(list: GeneratedItem[]): DesignRowData[] {
  const rows: DesignRowData[] = list.map((item, idx) => ({
    item,
    key: item.rgWorldHashKey ?? String(idx),
    name: NAMES[idx % NAMES.length],
    units: item.architecturalMetrics.ApartmentQuantityTotal,
    gia: item.architecturalMetrics.GIATotal,
    nia: item.architecturalMetrics.NIATotal,
    cost: item.costMetrics.summary.construction,
    co2: item.sustainabilityMetrics.summary.total,
    buildings: item.architecturalMetrics.buildingsCount,
    maxFloors: item.architecturalMetrics.FloorMax,
    leaders: [],
  }));
  if (rows.length) {
    const topGia = rows.reduce((a, b) => (b.gia > a.gia ? b : a));
    const lowCost = rows.reduce((a, b) => (b.cost < a.cost ? b : a));
    const greenest = rows.reduce((a, b) => (b.co2 < a.co2 ? b : a));
    topGia.leaders.push(`Top GIA · ${int(topGia.gia)} m²`);
    lowCost.leaders.push(`Lowest cost · ${eurM(lowCost.cost)}`);
    greenest.leaders.push(`Greenest · ${int(greenest.co2)} t CO₂`);
  }
  return rows;
}

export const ExploreView = observer(function ExploreView({
  board,
  sections,
  projectName,
  onCreateBrief,
  onEditBrief,
  onOpenMap,
  onFinalize,
  needed,
}: {
  board: BoardState;
  sections: BriefSectionState[];
  projectName: string;
  onCreateBrief: () => void;
  onEditBrief: (section: BriefSectionState) => void;
  onOpenMap: (ref: { sectionId: string; key: string }) => void;
  onFinalize: () => void;
  needed: number;
}) {
  const [sortKey, setSortKey] = useState<SortKey>("gia");
  const [detailed, setDetailed] = useState(false);
  const [mode3d, setMode3d] = useState(true);
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const hasContent = sections.length > 0;
  const latest = sections[sections.length - 1];
  const total = board.totalDesigns;
  const canFinalize = total >= 1 && total <= needed;

  return (
    <div className="h-full flex bg-[#f9fafb] overflow-hidden">
      <div className="grow overflow-y-auto">
        <div className="max-w-[900px] mx-auto px-6 py-5">
          {/* project header */}
          <div className="mb-6 flex items-end justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-[26px] leading-tight text-[#111827] truncate font-semibold">
                {projectName}
              </h1>
              <p className="text-[12px] text-[#6b7280] mt-1">
                {hasContent
                  ? "Explore options, then finalize"
                  : "No briefs yet · create one to generate designs"}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {[
                { k: "Designs", v: board.totalDesigns },
                { k: "Bookmarked", v: board.bookmarks.size },
                { k: "Revisions", v: sections.length },
              ].map((s) => (
                <div
                  key={s.k}
                  className="px-3.5 py-2 rounded-lg border border-[#e5e7eb] bg-white text-center min-w-[78px]"
                >
                  <div className="text-[20px] leading-none text-[#111827]">{s.v}</div>
                  <div className="text-[10px] uppercase tracking-wide text-[#9ca3af] mt-1">
                    {s.k}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {hasContent && (
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-[#9ca3af]">Sort</span>
                <select
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value as SortKey)}
                  className="h-8 min-w-[168px] pl-2.5 pr-7 rounded-sm border border-[#d1d5db] bg-white text-[12px] text-[#374151]"
                >
                  {SORTS.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <span className="text-[12px] text-[#9ca3af] tabular-nums">
                {board.totalDesigns} design{board.totalDesigns === 1 ? "" : "s"}
              </span>
            </div>
          )}

          {hasContent && (
            <div
              className={
                "mb-4 flex items-center gap-3 rounded-lg border px-4 py-3 " +
                (canFinalize ? "border-[#86efac] bg-[#f0fdf4]" : "border-[#e5e7eb] bg-white")
              }
            >
              <span
                className="w-8 h-8 shrink-0 grid place-items-center rounded-full text-white"
                style={{ background: canFinalize ? "#16a34a" : "#d1d5db" }}
              >
                <Icon d={canFinalize ? icons.check : icons.flag} size={16} />
              </span>
              <div className="grow min-w-0">
                <div className="text-[13px] font-semibold text-[#1f2937]">
                  {canFinalize ? "Ready to finalize" : `Narrow down to your top ${needed}`}
                </div>
                <div className="text-[11px] text-[#6b7280]">
                  {canFinalize
                    ? `${total} design${total === 1 ? "" : "s"} left — lock them in for reports.`
                    : `${total} designs · remove ${total - needed} more, then finalize for reports.`}
                </div>
              </div>
              <button
                onClick={onFinalize}
                disabled={!canFinalize}
                className={
                  "shrink-0 inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-lg text-[12px] font-semibold " +
                  (canFinalize ? "text-white" : "bg-[#f3f4f6] text-[#9ca3af] cursor-not-allowed")
                }
                style={canFinalize ? { background: "#16a34a" } : undefined}
              >
                Finalize <Icon d={icons.arrowR} size={15} />
              </button>
            </div>
          )}

          {/* designs tree */}
          {!hasContent ? (
            <DesignsEmpty onCreateBrief={onCreateBrief} />
          ) : (
            <div className="rounded-lg border border-[#e5e7eb] bg-white">
              <div className="flex items-center gap-2 px-3 h-11 border-b border-[#e5e7eb]">
                <span style={{ color: ACCENT }}>
                  <Icon d={icons.folder} size={16} />
                </span>
                <span className="text-[15px] font-semibold text-[#1f2937]">Designs</span>
                <span className="text-[11px] text-[#9ca3af]">
                  {board.totalDesigns} across {sections.length} revision
                  {sections.length === 1 ? "" : "s"}
                </span>
                <div className="ml-auto flex items-center gap-1.5">
                  <Seg
                    value={mode3d ? "3d" : "2d"}
                    onChange={(v) => setMode3d(v === "3d")}
                    options={[
                      { value: "3d", label: "3D" },
                      { value: "2d", label: "2D" },
                    ]}
                  />
                  <Seg
                    value={detailed ? "detailed" : "simple"}
                    onChange={(v) => setDetailed(v === "detailed")}
                    options={[
                      { value: "simple", label: "Simple" },
                      { value: "detailed", label: "Detailed" },
                    ]}
                  />
                </div>
              </div>
              <div className="px-3">
                {sections.map((section) => (
                  <RevisionFolder
                    key={section.id}
                    section={section}
                    board={board}
                    sortKey={sortKey}
                    detailed={detailed}
                    mode3d={mode3d}
                    onSelect={(key) => onOpenMap({ sectionId: section.id, key })}
                    onEditBrief={onEditBrief}
                  />
                ))}
                <div className="relative my-2">
                  <button
                    onClick={() => setNewMenuOpen((v) => !v)}
                    className="flex items-center gap-1.5 px-2 h-9 text-[12px] text-[#6b7280] hover:text-[#7c5cff]"
                  >
                    <Icon d={icons.plus} size={15} /> New revision
                    <Icon d={icons.chev} size={12} />
                  </button>
                  {newMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-30" onClick={() => setNewMenuOpen(false)} />
                      <div className="absolute left-2 bottom-full mb-1 z-40 w-60 bg-white border border-[#e5e7eb] rounded-md shadow-lg ring-1 ring-black/5 p-1.5">
                        <button
                          onClick={() => {
                            setNewMenuOpen(false);
                            if (latest) onEditBrief(latest);
                          }}
                          className="w-full text-left px-2 py-1.5 rounded-sm hover:bg-[#f3f4f6]"
                        >
                          <div className="text-[12px] font-medium text-[#374151]">
                            Copy latest revision
                          </div>
                          <div className="text-[10px] text-[#9ca3af]">
                            Iterate on {latest?.label} — tweak the brief or zones, saves as a new
                            revision.
                          </div>
                        </button>
                        <button
                          onClick={() => {
                            setNewMenuOpen(false);
                            onCreateBrief();
                          }}
                          className="w-full text-left px-2 py-1.5 rounded-sm hover:bg-[#f3f4f6]"
                        >
                          <div className="text-[12px] font-medium text-[#374151]">Start blank</div>
                          <div className="text-[10px] text-[#9ca3af]">
                            A fresh brief from scratch.
                          </div>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

const RevisionFolder = observer(function RevisionFolder({
  section,
  board,
  sortKey,
  detailed,
  mode3d,
  onSelect,
  onEditBrief,
}: {
  section: BriefSectionState;
  board: BoardState;
  sortKey: SortKey;
  detailed: boolean;
  mode3d: boolean;
  onSelect: (key: string) => void;
  onEditBrief: (section: BriefSectionState) => void;
}) {
  const id = section.id;
  const [collapsed, setCollapsed] = useState(false);
  const sort = SORTS.find((s) => s.key === sortKey) ?? SORTS[0];
  const ordered = deriveRows(board.shownOf(id))
    .slice()
    .sort((a, b) => (sort.get(a) - sort.get(b)) * sort.dir);
  const emptySlots = board.emptySlotsOf(id);
  const reserve = board.reserveCountOf(id);
  const generating = board.generatingFull(id);
  const rolling = board.isRolling(id);
  const rollingSlots = rolling ? board.rollingCount : 0;
  const rollAmt = board.rollAmt(id);
  const shownCount = board.shownOf(id).length;

  return (
    <div className="border-b border-[#e5e7eb] last:border-0 py-2.5">
      {/* heading + controls */}
      <div className="flex items-center justify-between mb-1.5 gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="flex items-center gap-1.5 min-w-0 text-left rounded-sm hover:bg-[#f3f4f6] -ml-1 pl-1 pr-1.5 py-0.5"
            title={collapsed ? "Expand" : "Collapse"}
          >
            <span
              className={
                "shrink-0 text-[#9ca3af] transition-transform " + (collapsed ? "-rotate-90" : "")
              }
            >
              <Icon d={icons.chev} size={14} />
            </span>
            <span className="shrink-0" style={{ color: ACCENT }}>
              <Icon d={icons.folder} size={15} />
            </span>
            <h2 className="text-[14px] font-semibold text-[#1f2937] truncate">{section.label}</h2>
          </button>
          {section.address && (
            <span className="text-[11px] text-[#9ca3af] truncate max-w-[160px]">
              {section.address}
            </span>
          )}
          <button
            onClick={() => onEditBrief(section)}
            className="inline-flex items-center gap-1 text-[11px] hover:underline shrink-0"
            style={{ color: ACCENT }}
          >
            <Icon d={icons.edit} size={12} /> Edit brief
          </button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-[#9ca3af] tabular-nums">
            {shownCount} design{shownCount === 1 ? "" : "s"}
            {reserve > 0 ? ` · ${reserve} in reserve` : ""}
          </span>
          {emptySlots > 0 ? (
            <div className="inline-flex items-center h-8 rounded-sm border border-[#d1d5db] bg-white overflow-hidden">
              <button
                onClick={() => board.removeSlot(id)}
                disabled={rolling}
                title="Remove an open slot"
                className="w-8 h-8 grid place-items-center text-[#6b7280] hover:bg-[#f3f4f6] disabled:text-[#e5e7eb]"
              >
                <Icon d={icons.minus} size={15} />
              </button>
              <span
                className="px-2 text-[11px] tabular-nums font-medium border-x border-[#e5e7eb] leading-8"
                style={{ color: ACCENT }}
              >
                {emptySlots} open
              </span>
              <button
                onClick={() => board.addSlot(id)}
                disabled={rolling || !board.canAddSlot(id)}
                title="Add an open slot"
                className="w-8 h-8 grid place-items-center text-[#6b7280] hover:bg-[#f3f4f6] disabled:text-[#e5e7eb]"
              >
                <Icon d={icons.plus} size={15} />
              </button>
            </div>
          ) : (
            board.canAddSlot(id) && (
              <button
                onClick={() => board.addSlot(id)}
                disabled={rolling}
                className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-sm border border-[#d1d5db] bg-white text-[12px] text-[#4b5563] hover:bg-[#f3f4f6]"
              >
                <Icon d={icons.plus} size={14} /> Add slot
              </button>
            )
          )}
          <button
            onClick={() => board.roll(id, rollAmt)}
            disabled={!board.canRoll(id)}
            title={
              board.reserveCountOf(id) === 0
                ? "No designs in reserve — edit the brief and generate again."
                : emptySlots === 0
                  ? "Add an open slot, then generate to fill it."
                  : `Generate ${rollAmt} design${rollAmt === 1 ? "" : "s"} from reserve`
            }
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-sm text-[12px] font-semibold border"
            style={
              !board.canRoll(id)
                ? { borderColor: "#e5e7eb", background: "#f3f4f6", color: "#d1d5db" }
                : rolling
                  ? { borderColor: ACCENT, background: ACCENT, color: "#fff" }
                  : {
                      borderColor: "rgba(124,92,255,0.6)",
                      background: "rgba(124,92,255,0.15)",
                      color: ACCENT,
                    }
            }
          >
            <Icon d={icons.generate} size={15} /> {rolling ? "Generating…" : `Generate ${rollAmt}`}
          </button>
        </div>
      </div>

      {!collapsed && generating && (
        <div className="h-1 w-full rounded bg-[#e5e7eb] mb-3 overflow-hidden">
          <div
            className="h-full transition-all"
            style={{
              width: `${Math.round(board.progress(id) * 100)}%`,
              background: ACCENT,
            }}
          />
        </div>
      )}

      {collapsed ? null : ordered.length === 0 && emptySlots === 0 && !rolling ? (
        <div className="text-[12px] text-[#9ca3af] px-2 py-8 text-center border border-dashed border-[#e5e7eb] rounded-sm">
          {generating ? "Generating designs…" : "No designs yet for this revision."}
        </div>
      ) : (
        <>
          {detailed && (
            <div className="flex items-center gap-3 px-2 mt-1 mb-1 text-[10px] uppercase tracking-wide text-[#9ca3af] border-b border-[#e5e7eb] pb-1">
              <span className="grow font-semibold text-[#6b7280]">Designs</span>
              <span className="w-12 text-right">Units</span>
              <span className="w-20 text-right">GIA</span>
              <span className="w-20 text-right">NIA</span>
              <span className="w-16 text-right">Cost</span>
              <span className="w-14 text-right">CO₂</span>
              <span className="w-[60px] shrink-0" />
            </div>
          )}
          <div className="flex flex-col gap-0.5">
            {Array.from({ length: rollingSlots }).map((_, i) => (
              <SpinnerRow key={"spin" + i} detailed={detailed} />
            ))}
            {ordered.map((d) => (
              <DesignRow
                key={d.key}
                d={d}
                detailed={detailed}
                mode3d={mode3d}
                isNew={board.recentlyRolled.has(d.key)}
                inspected={false}
                onSelect={() => onSelect(d.key)}
                bookmarked={board.bookmarks.has(d.key)}
                onToggleBookmark={() => board.toggleBookmark(d.key)}
                locked={board.locked.has(d.key)}
                onToggleLock={() => board.toggleLock(d.key)}
                onRemove={() => board.remove(id, d.item)}
              />
            ))}
            {!rolling &&
              Array.from({ length: emptySlots }).map((_, i) => (
                <div
                  key={"empty" + i}
                  className={
                    "group flex items-center gap-2.5 px-2 rounded-sm border border-dashed border-[#d1d5db] bg-[#f9fafb] " +
                    (detailed ? "h-[52px]" : "h-11")
                  }
                >
                  <span className="shrink-0 w-9 grid place-items-center text-[#d1d5db]">
                    <Icon d={icons.generate} size={16} />
                  </span>
                  <span className="grow text-[12px] text-[#9ca3af]">
                    Open slot — generate to fill
                  </span>
                  <button
                    onClick={() => board.removeSlot(id)}
                    title="Remove slot"
                    className="w-7 h-7 grid place-items-center rounded-sm border border-[#e5e7eb] text-[#9ca3af] opacity-0 group-hover:opacity-100 hover:text-[#4b5563] transition-opacity"
                  >
                    <Icon d={icons.x} size={13} />
                  </button>
                </div>
              ))}
          </div>
        </>
      )}
    </div>
  );
});

function DesignsEmpty({ onCreateBrief }: { onCreateBrief: () => void }) {
  return (
    <div className="rounded-lg border border-[#e5e7eb] bg-white">
      <div className="flex items-center gap-2.5 px-3 h-11 border-b border-[#e5e7eb]">
        <span style={{ color: ACCENT }}>
          <Icon d={icons.folder} size={16} />
        </span>
        <span className="text-[15px] font-semibold text-[#1f2937]">Designs</span>
        <button
          onClick={onCreateBrief}
          className="ml-auto inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-white text-[12px] font-semibold shadow-sm hover:opacity-90 transition-opacity"
          style={{ background: ACCENT }}
        >
          <Icon d={icons.plus} size={14} /> Create brief
        </button>
      </div>
      <div className="flex flex-col items-center justify-center text-center py-14 px-6">
        <div
          className="w-12 h-12 rounded-2xl grid place-items-center mb-4"
          style={{ background: "rgba(124,92,255,0.12)", color: ACCENT }}
        >
          <Icon d={icons.folder} size={22} />
        </div>
        <p className="text-[13px] font-medium text-[#374151] mb-1">No designs yet</p>
        <p className="text-[12px] text-[#9ca3af] max-w-[300px] leading-relaxed">
          Create a brief — set the site, draw your zones and define the mix — and Tessa generates
          designs here.
        </p>
      </div>
    </div>
  );
}

function Seg({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="inline-flex items-center rounded-md border border-[#e5e7eb] bg-[#f9fafb] p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          className={
            "px-2 h-6 rounded-[5px] text-[11px] font-medium transition-colors " +
            (value === o.value
              ? "bg-white text-[#1f2937] shadow-sm"
              : "text-[#9ca3af] hover:text-[#6b7280]")
          }
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function DesignRow({
  d,
  detailed,
  mode3d,
  isNew,
  inspected,
  onSelect,
  bookmarked,
  onToggleBookmark,
  locked,
  onToggleLock,
  onRemove,
}: {
  d: DesignRowData;
  detailed: boolean;
  mode3d: boolean;
  isNew: boolean;
  inspected: boolean;
  onSelect: () => void;
  bookmarked: boolean;
  onToggleBookmark: () => void;
  locked: boolean;
  onToggleLock: () => void;
  onRemove: () => void;
}) {
  return (
    <div
      onClick={onSelect}
      className={
        "group flex items-center gap-2.5 px-2 rounded-sm border cursor-pointer " +
        (detailed ? "h-[52px] " : "h-11 ") +
        (inspected
          ? "border-[#9ca3af] bg-[#f3f4f6]"
          : "border-transparent hover:bg-[#f3f4f6] hover:border-[#e5e7eb]")
      }
    >
      <DesignThumb
        item={d.item}
        mode={mode3d ? "3d" : "2d"}
        className={
          "shrink-0 object-cover rounded-md bg-white border border-[#e5e7eb] " +
          (detailed ? "w-11 h-11" : "w-9 h-9")
        }
      />
      <span className="min-w-0 grow">
        <span className="flex items-center gap-1.5 text-[12px] font-medium text-[#374151] overflow-hidden">
          <span className="shrink-0 truncate max-w-[150px]">{d.name}</span>
          {isNew && (
            <span className="shrink-0 text-[9px] font-semibold uppercase tracking-wide rounded-sm px-1 py-0.5 text-[#047857] bg-[#d1fae5] border border-[#6ee7b7]">
              New
            </span>
          )}
          {d.leaders.map((l) => (
            <span
              key={l}
              className="shrink-0 inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide rounded-sm px-1 py-0.5"
              style={{
                color: "#6b4cf0",
                background: "rgba(124,92,255,0.12)",
                border: "1px solid rgba(124,92,255,0.4)",
              }}
            >
              <Icon d={icons.star} size={10} /> {l}
            </span>
          ))}
        </span>
        {detailed && (
          <span className="block text-[10px] text-[#9ca3af] truncate">{d.buildings} buildings</span>
        )}
      </span>
      {detailed && (
        <>
          <span className="w-12 text-right text-[12px] tabular-nums text-[#374151] shrink-0">
            {int(d.units)}
          </span>
          <span className="w-20 text-right text-[12px] tabular-nums text-[#4b5563] shrink-0">
            {int(d.gia)}
          </span>
          <span className="w-20 text-right text-[12px] tabular-nums text-[#4b5563] shrink-0">
            {int(d.nia)}
          </span>
          <span className="w-16 text-right text-[12px] tabular-nums text-[#4b5563] shrink-0">
            {eurM(d.cost)}
          </span>
          <span className="w-14 text-right text-[12px] tabular-nums text-[#4b5563] shrink-0">
            {int(d.co2)}
          </span>
        </>
      )}
      <div className="shrink-0 flex items-center justify-end gap-1">
        <button
          title={bookmarked ? "Bookmarked" : "Bookmark"}
          onClick={(e) => {
            e.stopPropagation();
            onToggleBookmark();
          }}
          className={
            "w-7 h-7 grid place-items-center rounded-sm border transition-opacity " +
            (bookmarked ? "" : "opacity-0 group-hover:opacity-100")
          }
          style={
            bookmarked
              ? { borderColor: ACCENT, color: ACCENT, background: "rgba(124,92,255,0.1)" }
              : { borderColor: "#e5e7eb", color: "#9ca3af" }
          }
        >
          <Icon d={icons.bookmark} size={14} />
        </button>
        <button
          title={locked ? "Locked — protected from removal" : "Lock"}
          onClick={(e) => {
            e.stopPropagation();
            onToggleLock();
          }}
          className={
            "w-7 h-7 grid place-items-center rounded-sm border transition-opacity " +
            (locked ? "" : "opacity-0 group-hover:opacity-100")
          }
          style={
            locked
              ? { borderColor: ACCENT, color: ACCENT, background: "rgba(124,92,255,0.1)" }
              : { borderColor: "#e5e7eb", color: "#9ca3af" }
          }
        >
          <Icon d={locked ? icons.lock : icons.lockOpen} size={14} />
        </button>
        <button
          title={locked ? "Unlock to remove" : "Remove"}
          disabled={locked}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className={
            "w-7 h-7 grid place-items-center rounded-sm border border-[#e5e7eb] transition-opacity " +
            (locked
              ? "text-[#e5e7eb] cursor-not-allowed"
              : "text-[#9ca3af] hover:text-[#dc2626] hover:border-[#fca5a5] opacity-0 group-hover:opacity-100")
          }
        >
          <Icon d={icons.x} size={14} />
        </button>
      </div>
    </div>
  );
}

function SpinnerRow({ detailed }: { detailed: boolean }) {
  return (
    <div
      className={
        "flex items-center gap-2.5 px-2 rounded-sm border border-[rgba(124,92,255,0.35)] bg-[rgba(124,92,255,0.04)] " +
        (detailed ? "h-[52px]" : "h-11")
      }
    >
      <span
        className={
          "shrink-0 grid place-items-center rounded-md bg-[#ece8ff] overflow-hidden " +
          (detailed ? "w-11 h-11" : "w-9 h-9")
        }
      >
        <span className="flex items-end gap-0.5 h-5">
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              className="w-[3px] rounded-[1px] bg-[#7c5cff] animate-bounce"
              style={{ height: 7 + ((i * 5) % 12), animationDelay: `${i * 110}ms` }}
            />
          ))}
        </span>
      </span>
      <span className="grow flex flex-col gap-1.5">
        <span className="h-2.5 w-24 rounded-sm bg-[#ece8ff] animate-pulse" />
        <span className="h-2 w-16 rounded-sm bg-[#f3f4f6] animate-pulse" />
      </span>
    </div>
  );
}
